import { getAccessToken } from '@/action/supabase';
import type { PublicPuzzle, PuzzleChat } from '@/utils/util_types';
import { applyMultiLangToPuzzle } from '@/utils/util_puzzles';

export type ChatServiceErrorCode = 'need_login' | 'need_recharge' | 'not_found' | 'chat_closed' | 'network' | 'error';

export class ChatServiceError extends Error {
    constructor(message: string, public readonly code: ChatServiceErrorCode, public readonly status?: number) {
        super(message);
        this.name = 'ChatServiceError';
    }
}

export async function authenticatedRequest<T>(url: string, init?: RequestInit): Promise<T> {
    const token = await getAccessToken();
    if (!token) throw new ChatServiceError('Authentication required', 'need_login', 401);

    let response: Response;
    try {
        response = await fetch(url, {
            ...init,
            headers: {
                ...init?.headers,
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        });
    } catch (error) {
        throw new ChatServiceError(error instanceof Error ? error.message : 'Network request failed', 'network');
    }

    const body = await response.json().catch(() => ({})) as Record<string, unknown>;
    if (!response.ok) {
        const resultCode = typeof body.result_code === 'string' ? body.result_code : '';
        const code: ChatServiceErrorCode = resultCode === 'need_login' || resultCode === 'need_recharge' || resultCode === 'chat_closed'
            ? resultCode
            : response.status === 404 ? 'not_found' : 'error';
        throw new ChatServiceError(
            typeof body.error === 'string' ? body.error : `Request failed (${response.status})`,
            code,
            response.status,
        );
    }
    return body as T;
}

export async function resolveOrStartChat(puzzleId: string): Promise<{ chatId: string; created: boolean }> {
    const response = await authenticatedRequest<{ chat_id: string; created: boolean }>('/api/chats/resolve', {
        method: 'POST',
        body: JSON.stringify({ puzzle_id: puzzleId }),
    });
    return { chatId: response.chat_id, created: response.created };
}

export async function loadPuzzleChat(chatId: string, language: string): Promise<PuzzleChat> {
    const url = `/api/chats/${encodeURIComponent(chatId)}?language=${encodeURIComponent(language)}`;

    // A newly-created chat can briefly lag behind the list/read replica. Retry
    // only this idempotent read so a transient 404 does not break navigation.
    for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
            const response = await authenticatedRequest<{ chat: PuzzleChat }>(url);
            return response.chat;
        } catch (error) {
            if (!(error instanceof ChatServiceError) || error.code !== 'not_found' || attempt === 2) throw error;
            await new Promise((resolve) => setTimeout(resolve, 200 * (attempt + 1)));
        }
    }

    throw new ChatServiceError('Chat not found', 'not_found', 404);
}

export async function loadUserPuzzleChats(language: string): Promise<PuzzleChat[]> {
    const response = await authenticatedRequest<{ chats: PuzzleChat[] }>(
        `/api/chats?language=${encodeURIComponent(language)}`,
    );
    return response.chats;
}

export function splitPuzzleChats(chats: PuzzleChat[]) {
    const sorted = [...chats].sort((a, b) => {
        const left = a.update_timestamp || a.timestamp || '';
        const right = b.update_timestamp || b.timestamp || '';
        return right.localeCompare(left);
    });
    return {
        active: sorted.filter((chat) => chat.status === 'active'),
        completed: sorted.filter((chat) => chat.status === 'completed' || chat.status === 'revealed'),
    };
}

export type ContinueStoryResult = {
    answer_text: string;
    answer_type: 'yes' | 'no' | 'both' | 'unrelated';
    keypoints_state: (boolean | null)[];
    status: 'active' | 'completed';
    truth?: string;
    coins?: number;
};

export async function continuePuzzleChat(chatId: string, question: string, language: string): Promise<ContinueStoryResult> {
    const response = await authenticatedRequest<{ result: ContinueStoryResult }>('/api/spg/continue-story', {
        method: 'POST',
        body: JSON.stringify({ chat_id: chatId, question, language }),
    });
    return response.result;
}

export async function setPuzzleLike(puzzleId: string, liked: boolean) {
    return authenticatedRequest<{ liked: boolean; liked_num: number }>(
        `/api/puzzles/${encodeURIComponent(puzzleId)}/like`,
        { method: 'POST', body: JSON.stringify({ liked }) },
    );
}

export async function reportPuzzle(puzzleId: string, reason: string, extraText: string) {
    await authenticatedRequest<{ reported: boolean }>(
        `/api/puzzles/${encodeURIComponent(puzzleId)}/report`,
        { method: 'POST', body: JSON.stringify({ reason, extra_text: extraText }) },
    );
}

export type RevealPrice = {
    base_price: number;
    invested: number;
    rounds: number;
    solved: number;
    price: number;
    full_price: number;
    is_free: boolean;
    already: boolean;
};

export async function getRevealPrice(chatId: string): Promise<RevealPrice> {
    const response = await authenticatedRequest<{ price: RevealPrice }>('/api/spg/reveal-price', {
        method: 'POST',
        body: JSON.stringify({ chat_id: chatId }),
    });
    return response.price;
}

export async function revealAnswer(chatId: string): Promise<{ cost?: number; is_free?: boolean }> {
    const response = await authenticatedRequest<{ result: { cost?: number; is_free?: boolean } }>('/api/spg/reveal-answer', {
        method: 'POST',
        body: JSON.stringify({ chat_id: chatId }),
    });
    return response.result ?? {};
}

export async function deletePuzzleChat(chatId: string) {
    await authenticatedRequest<{ deleted: boolean }>(
        `/api/chats/${encodeURIComponent(chatId)}`,
        { method: 'DELETE' },
    );
}

export async function recommendNextPuzzle(options: {
    language: string;
    currentDifficulty?: number | null;
    preferEasier?: boolean;
    excludePuzzleId?: string;
}) {
    const params = new URLSearchParams();
    if (options.currentDifficulty != null) params.set('current_difficulty', String(options.currentDifficulty));
    if (options.preferEasier) params.set('prefer_easier', 'true');
    if (options.excludePuzzleId) params.set('exclude_puzzle_id', options.excludePuzzleId);
    const response = await authenticatedRequest<{ puzzle: PublicPuzzle | null }>(`/api/puzzles/recommend?${params}`);
    return response.puzzle ? applyMultiLangToPuzzle(response.puzzle, options.language) : null;
}
